/**
 * focusStore — "Focus until …": holds back pop-up notifications for a while.
 *
 * Notifications still arrive in the bell and on Today; only the interrupting
 * part (native/browser pop-ups) waits. @mentions and calls always get through.
 * Kept on this device only.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Notification types that interrupt even during focus */
export const URGENT_TYPES = new Set(['mention', 'call']);

interface FocusState {
  /** Epoch ms when focus ends, or null when off */
  until: number | null;
  /** Pop-ups held back since focus started */
  held: number;
  start: (until: number) => void;
  stop: () => void;
  noteHeld: () => void;
}

export const useFocusStore = create<FocusState>()(
  persist(
    (set) => ({
      until: null,
      held: 0,
      start: (until) => set({ until, held: 0 }),
      stop: () => set({ until: null, held: 0 }),
      noteHeld: () => set(s => ({ held: s.held + 1 })),
    }),
    { name: 'eurion-focus' },
  ),
);

export function isFocusActive(until: number | null, now = Date.now()): boolean {
  return until != null && until > now;
}

/** Should a notification of this type pop up right now? */
export function shouldInterrupt(type: string | undefined, now = Date.now()): boolean {
  const { until } = useFocusStore.getState();
  if (!isFocusActive(until, now)) return true;
  return URGENT_TYPES.has(type ?? '');
}

/** Focus presets: 30 min, 1 h, 2 h, and "until tomorrow 08:00" */
export function focusPresets(now = new Date()): { id: '30m' | '1h' | '2h' | 'tomorrow'; until: number }[] {
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 8, 0);
  return [
    { id: '30m', until: now.getTime() + 30 * 60_000 },
    { id: '1h', until: now.getTime() + 60 * 60_000 },
    { id: '2h', until: now.getTime() + 120 * 60_000 },
    { id: 'tomorrow', until: tomorrow.getTime() },
  ];
}
